'use client';

import { brandCode } from '@/brands';
import { motion } from '@/libs';

import { useUserLoginForm } from '../hooks';

import {
  Button,
  FormField,
  Icon,
  InputForm,
  Separator,
  formatDocument,
  formatPhone,
} from '../../ui';

import type { RegistrationSeed } from '../hooks/useUserRegisterForm';

type UserData = {
  name: string;
  socialName?: string;
  document: string;
  phone: string;
  customerId: string;
  hasAccount: boolean;
  hasMail: boolean;
  hasAddress: boolean;
};

type UserLoginFormProps = {
  onLoginSuccess: (data: UserData, options?: { skipCheckoutRedirect?: boolean }) => void;
  onNeedsRegistration: (seed: RegistrationSeed) => void;
  onStartRecovery: () => void;
};

export const UserLoginForm = ({
  onLoginSuccess,
  onNeedsRegistration,
  onStartRecovery,
}: UserLoginFormProps) => {
  const {
    control,
    errors,
    isLoading,
    isCheckingCpf,
    cpfStatusError,
    isCreateMode,
    handleSubmit,
    getSubmitLoadingText,
  } = useUserLoginForm({
    onLoginSuccess,
    onNeedsRegistration,
  });

  return (
    <motion.div
      data-cy="user-login-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-4"
    >
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        className="text-sm text-foreground text-center"
      >
        Preencha os campos abaixo para acessar sua conta.
      </motion.p>

      <motion.form
        data-cy="user-login-form-form"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        onSubmit={handleSubmit}
        className="flex flex-col gap-y-5 mx-auto w-full md:max-w-[336px]"
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.3 }}
        >
          <FormField error={errors.document} label="CPF">
            <InputForm
              data-cy="user-login-form-document-input"
              control={control}
              name="document"
              placeholder="000.000.000-00"
              formatFunction={formatDocument}
              inputMode="numeric"
              autoComplete="on"
            />
          </FormField>
          {isCheckingCpf && (
            <p className="mt-1 text-xs text-foreground/60">Verificando CPF...</p>
          )}
          {isCreateMode && (
            <p
              data-cy="user-login-form-cpf-not-found"
              className="mt-1 text-xs text-foreground/70"
            >
              Cadastro não encontrado. Para te auxiliar no cadastro, podemos consultar
              bases públicas e privadas para validar e complementar seus dados cadastrais.
            </p>
          )}
          {cpfStatusError && (
            <p
              data-cy="user-login-form-cpf-status-error"
              className="mt-1 text-xs text-red-500"
            >
              {cpfStatusError}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <Separator className="bg-foreground/10" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <FormField error={errors.phone} label="Telefone">
            <InputForm
              data-cy="user-login-form-phone-input"
              control={control}
              name="phone"
              placeholder="(00) 00000-0000"
              formatFunction={formatPhone}
              inputMode="numeric"
              autoComplete="tel"
              disabled={isCheckingCpf}
            />
          </FormField>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <Separator className="bg-foreground/10" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        >
          <Button
            data-cy="user-login-form-submit-button"
            type="submit"
            loading={isLoading}
            disabled={isLoading || isCheckingCpf}
            loadingText={getSubmitLoadingText()}
            variant="primary"
            size="large"
            className="h-12 w-full flex items-center text-background text-base uppercase"
          >
            <span className="mx-auto">
              {isCreateMode ? 'Criar Conta' : 'Acessar Conta'}
            </span>
            <Icon.ChevronRight width={24} height={24} />
          </Button>
        </motion.div>

        {brandCode !== 'sbt' && !isCreateMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.3 }}
            className="text-center"
          >
            <Button
              type="button"
              variant="link"
              className="mx-auto"
              onClick={onStartRecovery}
            >
              Esqueceu o telefone?
            </Button>
          </motion.div>
        )}
      </motion.form>
    </motion.div>
  );
};
